/**
 * Resume saved quotes from Activity / dashboard into the correct buy journey step.
 */

import { getMotorQuote } from '@/services/customerMotorApi';
import { getPaQuotation, isPaOpenQuote, recalculatePaPremium } from '@/services/agentPaApi';
import agentTravelApi, { isRemoteTravelQuoteId } from '@/services/agentTravelApi';
import { isCustomerMotorV2Enabled } from '@/utils/motorChannel';
import {
  isPaQuoteAwaitingIssuance,
  isPaQuoteIssued,
  resolvePaCustomerResumeRoute,
} from '@/utils/paQuoteResume';
import {
  saveMotorPendingQuote, savePaPendingQuote, saveTravelPendingQuote, loadTravelPendingQuote,
} from '@/utils/saleCache';

function toastError(toast, message, title) {
  if (toast && typeof toast.error === 'function') {
    toast.error(message, title);
  }
}

function toastWarning(toast, message, title) {
  if (toast && typeof toast.warning === 'function') {
    toast.warning(message, title);
  }
}

export function resolveMotorRiskId(quote) {
  const risks = quote?.details?.risks || [];
  const selected = risks.find((r) => r.selected === 'Y');
  const recommended = risks.find((r) => r.recommended === 'Y');
  const risk = selected || recommended || risks[0];
  return risk?.riskId;
}

export async function resumeMotorQuote(quote, { router, email, toast } = {}) {
  const quoteId = quote?.quoteId;
  const riskId = resolveMotorRiskId(quote);
  if (!quoteId) {
    toastError(toast, 'Invalid motor quote', 'Error');
    return;
  }
  if (!riskId) {
    toastError(toast, 'Could not determine cover type for this quote', 'Motor');
    return;
  }

  if (isCustomerMotorV2Enabled()) {
    const res = await getMotorQuote(quoteId);
    if (res.success && res.data) {
      saveMotorPendingQuote(email, { ...res.data, riskId, quoteId });
    }
    await router.push({
      name: 'CustomerMotorApply',
      query: { quoteId: String(quoteId), riskId: String(riskId) },
    });
    return;
  }

  await router.push({
    name: 'BuyCover',
    query: { q: String(quoteId), b: String(riskId) },
  });
}

export async function resumePaQuote(quote, { router, email, toast } = {}) {
  let data = { ...quote };
  if (!data?.quoteId) {
    toastError(toast, 'Invalid PA quote', 'Error');
    return;
  }

  const res = await getPaQuotation(data.quoteId, 'customer');
  if (res.success && res.data) {
    data = { ...data, ...res.data };
    if (data.optionCode && data.courseDuration) {
      const recalc = await recalculatePaPremium(data.optionCode, data.courseDuration, data.quoteId);
      if (recalc.success && recalc.data?.premium != null) {
        data = { ...data, premium: recalc.data.premium };
      }
    }
  } else if (res.message) {
    toastWarning(toast, res.message, 'Students PA');
  }

  if (email) savePaPendingQuote(email, data);

  if (isPaQuoteIssued(data)) {
    await router.push({ name: 'MyPolicies' });
    return;
  }
  if (isPaQuoteAwaitingIssuance(data)) {
    await router.push({
      name: 'CustomerPaFinalization',
      params: { quoteId: String(data.quoteId) },
    });
    return;
  }

  if (!isPaOpenQuote(data)) {
    toastWarning(toast, 'This quote is no longer available to resume.', 'Students PA');
    return;
  }

  await router.push(resolvePaCustomerResumeRoute(data));
}

export async function resumeHealthQuote(quote, { router } = {}) {
  if (!quote?.id) return;
  await router.push({
    name: 'BuyCover',
    query: { q: String(quote.id), b: 'health' },
  });
}

export async function resumeDpQuote(quote, { router } = {}) {
  if (!quote?.id) return;
  await router.push({
    name: 'BuyCover',
    query: { q: String(quote.id), b: 'dp' },
  });
}

export function buildTravelQuotePayload(travelQuote, getCoverageAmountByRegion) {
  return {
    ...travelQuote,
    destination: travelQuote.destination || travelQuote.region || 'Unknown Destination',
    travel_type: travelQuote.travel_type || travelQuote.plan_type || 'Unknown Type',
    start_date: travelQuote.start_date || travelQuote.travel_start_date || 'TBD',
    end_date: travelQuote.end_date || travelQuote.travel_end_date || 'TBD',
    number_of_travelers: travelQuote.number_of_travelers || travelQuote.travelers || 1,
    coverage_amount: getCoverageAmountByRegion(
      travelQuote.region || travelQuote.destination,
      travelQuote.plan_type || travelQuote.travel_type,
    ),
    premium: travelQuote.premium || travelQuote.revised_price || 0,
    region: travelQuote.region || travelQuote.destination || 'Unknown',
    plan_type: travelQuote.plan_type || travelQuote.travel_type || 'individual',
  };
}

export async function resumeAgentTravelQuote(travelQuote, { router, email, toast } = {}) {
  const quoteId = travelQuote?.id || travelQuote?.quoteId;
  if (!quoteId) {
    toastError(toast, 'Invalid travel quote', 'Error');
    return Promise.resolve();
  }
  let snapshot = loadTravelPendingQuote(email) || {};
  const res = await agentTravelApi.getQuote(quoteId);
  if (res.success && res.data) {
    const d = res.data;
    snapshot = {
      ...snapshot,
      quoteId,
      prodCode: snapshot.prodCode || d.prod_code,
      productLabel: snapshot.productLabel || d.plan_type,
      destinationCountry: snapshot.destinationCountry || d.destination_country,
      startDate: snapshot.startDate || d.travel_start_date,
      endDate: snapshot.endDate || d.travel_end_date,
      grossUsd: snapshot.grossUsd || d.premiumUsd || d.premium || d.grossUsd,
      kshAmount: snapshot.kshAmount || d.kshAmount || d.ksh_amount,
      fxRate: snapshot.fxRate || d.fxRate || d.fx_rate || 130,
      quoteTravellersJson: snapshot.quoteTravellersJson || d.quoteTravellersJson,
      travellerCount: snapshot.travellerCount || d.traveller_count || 1,
      customerEmail: snapshot.customerEmail || d.email_address,
    };
  } else {
    snapshot = { ...snapshot, quoteId };
  }
  saveTravelPendingQuote(email, snapshot);
  await router.push({
    name: 'AgentTravelSale',
    query: { quoteId: String(quoteId), journey: 'quote' },
  });
  return Promise.resolve();
}

export async function resumeTravelQuote(travelQuote, {
  router, store, toast, getCoverageAmountByRegion, channel, email,
} = {}) {
  const quoteId = travelQuote?.quoteId || travelQuote?.id;
  if (!quoteId) {
    toastError(toast, 'Invalid travel quote', 'Error');
    return Promise.resolve();
  }

  const isAgent = channel === 'agent'
    || (travelQuote.journey && travelQuote.journey !== 'direct');

  if (isAgent) {
    return resumeAgentTravelQuote(
      { ...travelQuote, quoteId },
      { router, email: email || travelQuote.email || travelQuote.email_address, toast },
    );
  }

  if (isRemoteTravelQuoteId(quoteId)) {
    await router.push({
      name: 'CustomerTravelSale',
      query: { quoteId: String(quoteId), journey: 'direct' },
    });
    return Promise.resolve();
  }

  if (!travelQuote?.id) {
    toastError(toast, 'Invalid travel quote data', 'Error');
    return Promise.resolve();
  }
  try {
    const mappedQuote = buildTravelQuotePayload(travelQuote, getCoverageAmountByRegion);
    if (store && typeof store.commit === 'function') {
      store.commit('rfq/setTravelQuoteResponse', mappedQuote);
    }
    await router.push({ name: 'TravelQuoteSummary', query: { q: travelQuote.id } });
  } catch {
    try {
      await router.push({ name: 'PersonalDetailsTravel', query: { q: travelQuote.id } });
    } catch {
      toastError(toast, 'Unable to open travel quote. Please try again.', 'Navigation Error');
    }
  }
  return Promise.resolve();
}
